import { type QueryClient, useQuery } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Link,
  Outlet,
  type RouterHistory,
  redirect,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import type { SessionUser } from '@vision360/contracts';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/components/app-shell';
import { LoadingScreen } from '@/components/status-screen';
import { AlertsPage } from '@/features/alerts/alerts-page';
import { LoginPage } from '@/features/auth/login-page';
import { SESSION_KEY, sessionQuery } from '@/features/auth/session-query';
import { DashboardPage } from '@/features/dashboards/dashboard-page';
import { RulesPage } from '@/features/rules/rules-page';
import { SettingsPage } from '@/features/settings/settings-page';
import { SourcesPage } from '@/features/sources/sources-page';
import type { Session } from '@/lib/auth/session';
import { useSession } from '@/lib/auth/session-context';

export interface RouterContext {
  queryClient: QueryClient;
  session: Session;
}

/**
 * Only same-origin paths may be a post-login destination, so a crafted
 * ?redirect= cannot bounce the user to another site.
 */
export function safeRedirect(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  if (!value.startsWith('/') || value.startsWith('//') || value.includes('\\')) return undefined;
  // Never return to the login page itself: that nests redirects without end.
  if (value === '/login' || value.startsWith('/login?') || value.startsWith('/login/')) {
    return undefined;
  }
  return value;
}

function NotFound() {
  const { t } = useTranslation('common');
  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-3 p-4 text-center">
      <h1 className="text-2xl font-semibold">{t('notFound.title')}</h1>
      <p className="text-muted-foreground">{t('notFound.description')}</p>
      <Link to="/" className="text-primary underline-offset-4 hover:underline">
        {t('notFound.back')}
      </Link>
    </main>
  );
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: NotFound,
});

type LoginSearch = { redirect?: string };

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search: Record<string, unknown>): LoginSearch => {
    const target = safeRedirect(search['redirect']);
    return target ? { redirect: target } : {};
  },
  beforeLoad: ({ context, search }) => {
    if (context.queryClient.getQueryData(SESSION_KEY)) {
      throw redirect({ href: search.redirect ?? '/' });
    }
  },
  component: function LoginRoute() {
    const search = loginRoute.useSearch();
    const navigate = useNavigate();
    return (
      <LoginPage
        onSignedIn={() => void navigate({ href: search.redirect ?? '/', replace: true })}
      />
    );
  },
});

/** Every route below requires a session; losing it mid-visit sends you to /login. */
const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authed',
  beforeLoad: ({ context, location }) => {
    if (!context.queryClient.getQueryData<SessionUser | null>(SESSION_KEY)) {
      const target = safeRedirect(location.href);
      throw redirect({
        to: '/login',
        search: target && target !== '/' ? { redirect: target } : {},
      });
    }
  },
  component: function AuthedLayout() {
    const { t } = useTranslation('common');
    const session = useSession();
    const { data: user } = useQuery(sessionQuery(session));
    const navigate = useNavigate();
    const href = useRouterState({ select: (s) => s.location.href });
    // Navigate once per sign-out. Without this guard the effect re-fires as the
    // URL changes mid-transition and nests ?redirect= inside itself.
    const leaving = useRef(false);

    useEffect(() => {
      if (user !== null || leaving.current) return;
      leaving.current = true;
      const target = safeRedirect(href);
      void navigate({
        to: '/login',
        search: target && target !== '/' ? { redirect: target } : {},
        replace: true,
      });
    }, [user, href, navigate]);

    if (!user) return <LoadingScreen label={t('loading.page')} />;
    return (
      <AppShell user={user}>
        <Outlet />
      </AppShell>
    );
  },
});

/** The default dashboard of mode web (TechSpec §9.5). */
const homeRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/',
  component: () => <DashboardPage />,
});

const dashboardRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/d/$dashboardId',
  component: function DashboardRoute() {
    const { dashboardId } = dashboardRoute.useParams();
    return <DashboardPage dashboardId={dashboardId} />;
  },
});

const sourcesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/sources',
  component: () => <SourcesPage />,
});

const alertsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/alerts',
  component: () => <AlertsPage />,
});

const rulesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/rules',
  component: () => <RulesPage />,
});

const settingsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/settings',
  component: SettingsPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  authedRoute.addChildren([
    homeRoute,
    dashboardRoute,
    sourcesRoute,
    alertsRoute,
    rulesRoute,
    settingsRoute,
  ]),
]);

export function createAppRouter(context: RouterContext, history?: RouterHistory) {
  return createRouter({
    routeTree,
    context,
    defaultPreload: 'intent',
    ...(history ? { history } : {}),
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
